import { ChangeDetectorRef, Directive, DoCheck, Input, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { AppExtensionService } from '@alfresco/aca-shared';
import { Store } from '@ngrx/store';
import { AppStore, SnackbarErrorAction } from '@alfresco/aca-shared/store';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ContentActionRef, ExtensionService } from '@alfresco/adf-extensions';
import { MinimalNodeEntity, ResultBuckets } from '@alfresco/js-api';
import { ActivatedRoute, Router } from '@angular/router';
import { ImageResolver} from '@alfresco/adf-content-services';
import { PageComponent } from 'app/src/app/components/page.component';
import { ContentManagementService } from 'app/src/app/services/content-management.service';
import { CustomViewerConfig } from '../../models/custom-viewer-config';
import { takeUntil } from 'rxjs/operators';
import { CustomListService } from '../../services/custom-list.service';
import { CustomSearchFilterComponent } from './custom-search-filter/custom-search-filter.component';
import { AldenService } from '../../services/alden.service';
import { CustomUserPreferencesService } from '../../services/custom-user-preferences-service';

@Directive({
    selector: '[]'
})
export abstract class ListComponent extends PageComponent implements OnInit, DoCheck {
  isSmallScreen = false;
  selectedNode: MinimalNodeEntity;

  //Nombre de la configuración del visor en el json
  @Input()
  viewerConfigId: string;

  viewerConfig: CustomViewerConfig;
  showFilters: boolean = false;

  previousActions: Array<ContentActionRef> = [];

  @Input()
  customImageResolver: ImageResolver;

  @Input()
  defaultFilterValues: { id: string, defaultValue: any}[];

  @ViewChild(CustomSearchFilterComponent) smtSearchFilter: CustomSearchFilterComponent;
  
  constructor(
    store: Store<AppStore>,
    content: ContentManagementService,
    extensions: AppExtensionService,
    protected extensionService: ExtensionService,
    protected breakpointObserver: BreakpointObserver,
    protected router: Router,
    protected customListService: CustomListService,
    protected cdRef: ChangeDetectorRef,
    protected location: Location, 
    protected route: ActivatedRoute,
    protected aldenService: AldenService,
    protected customUserPreferencesService: CustomUserPreferencesService
  ) {
    super(store, extensions, content);
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
     
    this.customUserPreferencesService.setViewerId(this.viewerConfigId);

    const defaultSearchID = this.customUserPreferencesService.getDefaultSearch();
    
    this.defaultFilterValues = this.defaultFilterValues ?? [];
    if (!defaultSearchID) {
      // Obtenemos los filtros por defecto
      const defaultFilterValues: any[] = this.location?.getState()['defaultFilterValues'] ?? [];
      const queryParams = this.route?.snapshot?.queryParams;
      for (const key in queryParams) {
        if (key != 'location' && queryParams.hasOwnProperty(key) 
          && !this.defaultFilterValues.some((d: { id: string; defaultValue: any; }) => d.id === `${this.viewerConfigId}.${key}`)) {
          defaultFilterValues.push({ id: `${this.viewerConfigId}.${key}`, defaultValue: queryParams[key] });
        }
      }
      this.defaultFilterValues = [...this.defaultFilterValues, ...defaultFilterValues];
    } else {
      await this.loadDefaultSearch(defaultSearchID).catch( err => {
          if(err.error.status === 404)
            this.customUserPreferencesService.setDefaultSearch("")
          else
            this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.SEARCH_NOT_FOUND'));
        });
    }
   
    
    //Suscribirse al showHideFiltersHeaderTable
    this.customListService.showHideFiltersHeaderTable.pipe(takeUntil(this.onDestroy$)).subscribe(
      () => {
        this.showFilters = !this.showFilters;
    });

    this.subscriptions = this.subscriptions.concat([
      this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape]).subscribe((result) => {
        this.isSmallScreen = result.matches;
      })
    ]);

    this.viewerConfig = this.extensionService.features["custom-lists"][this.viewerConfigId];

    if(this.viewerConfig?.disableNewFilters){
      this.viewerConfig.columns.map(column => {
        if(column.search.settings){
          column.search.settings.useChips = false;
          column.search.settings.allowOperatorChange = false;
          column.search.settings.nullable = false;
        }
      });
    }
  }

  //Forma de poder escuchar un elemento de la clase padre sin modificar la clase padre
  ngDoCheck(): void {
    if(this.viewerConfig?.showOnlyMyActions && this.actions.length > 0){
      if (!this.compareArrays(this.actions, this.previousActions)) {
        this.previousActions = [...this.actions]; //Clon para trabajar con él
        //Revisar las acciones
        this.previousActions = this.actions.filter(action => { 
          const actionToShow = this.hasToShowAction(action);
          // Revisamos también los hijos
          if(actionToShow && action.children?.length > 0) {
            action.children = action.children.filter((a) => this.hasToShowAction(a));
          }
          return actionToShow;
        });
        //Ya podemos sustituir acciones
        this.actions = this.previousActions;
        this.cdRef.detectChanges();
      }
    }
  }

  private hasToShowAction(action: ContentActionRef): boolean {
    return this.viewerConfig?.showOnlyMyActions.some((a) => action.id.includes(a)) 
          && !this.viewerConfig?.excludeActions?.some((a) => action.id.includes(a)) 
          && !action.disabled 
          && (!action.rules || (action.rules.enabled !== "false" && this.extensions.evaluateRule(action.rules.visible)));
  }

  private compareArrays(arr1: ContentActionRef[], arr2: ContentActionRef[]): boolean {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, i) => item === arr2[i]); // Comparación por referencia
  }

  navigateTo(node: MinimalNodeEntity) {
    if (node && node.entry) {
      this.selectedNode = node;
      this.showPreview(node, { location: this.router.url });
    }
  }

  onNodeDoubleClick(event: Event) {
    this.navigateTo((event as CustomEvent).detail?.node);
  }

  removeDefaultValues() {
    this.smtSearchFilter.removeDefaultValues();
  }

  refreshToolbarActions() {
    // Comprobamos de nuevo la visibilidad de las acciones. Volverá a ejecutarse ngDoCheck
    this.extensions
      .getAllowedToolbarActions()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((actions) => {
        this.actions = actions;
      });
  }

  refreshFilters(buckets: ResultBuckets[]){
    this.smtSearchFilter.refreshFilters(buckets);
  }

  async loadDefaultSearch(id:string){
    const file = await this.aldenService.getContentOfFile(id);
    const jsonContent = JSON.parse(await file.text());
    const defaultFilterValues: { id: string, defaultValue: any}[] = [];

    Object.entries(jsonContent).forEach(([key, value]) => {
      defaultFilterValues.push({ id: key, defaultValue: value });
    });

    this.defaultFilterValues.push(...defaultFilterValues)
  }
}
